/**
 * CEO Command Center v11 - SQLite-backed Data Store
 *
 * Zustand store that uses local SQLite (CR-SQLite) instead of Airtable.
 * This is a drop-in replacement for the Airtable-based useDataStore.
 *
 * @module stores/dataStoreSQLite
 * @version 11.0.0
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { databaseService, syncService } from '@/services/sync';
import { SignalEngine } from '@/services/signalEngine';
import { useSyncStore } from './syncStore';
import { useInitiativeStore } from './initiativeStore';
import type {
  Task,
  Deal,
  Client,
  Owner,
  CashFlowRecord,
  Signal,
  Investor,
  MAURecord,
} from '../types';

// ============================================
// TYPES
// ============================================

interface DataState {
  // Data
  tasks: Task[];
  deals: Deal[];
  clients: Client[];
  owners: Owner[];
  cashflow: CashFlowRecord[];
  investors: Investor[];
  mauData: MAURecord[];
  signals: Signal[];

  // Loading states
  loading: boolean;
  loadingTasks: boolean;
  loadingDeals: boolean;
  error: string | null;

  // Last refresh timestamp
  lastRefresh: string | null;

  // Actions
  fetchAll: () => Promise<void>;
  fetchTasks: () => Promise<void>;
  fetchDeals: () => Promise<void>;
  refreshSignals: () => void;

  // Task actions
  createTask: (task: Partial<Task>) => Promise<Task>;
  updateTask: (id: string, updates: Partial<Task>) => Promise<Task>;
  deleteTask: (id: string) => Promise<void>;
  toggleTaskStatus: (id: string) => Promise<void>;
  toggleTaskSignal: (id: string) => Promise<void>;

  // Deal actions
  createDeal: (deal: Partial<Deal>) => Promise<Deal>;
  updateDeal: (id: string, updates: Partial<Deal>) => Promise<Deal>;
  deleteDeal: (id: string) => Promise<void>;

  // Client actions
  createClient: (client: Partial<Client>) => Promise<Client>;
  updateClient: (id: string, updates: Partial<Client>) => Promise<Client>;
  deleteClient: (id: string) => Promise<void>;

  // Owner actions
  createOwner: (owner: Partial<Owner>) => Promise<Owner>;
  updateOwner: (id: string, updates: Partial<Owner>) => Promise<Owner>;
  deleteOwner: (id: string) => Promise<void>;

  // Investor actions
  addInvestor: (investor: Investor) => Promise<void>;
  updateInvestor: (id: string, updates: Partial<Investor>) => Promise<void>;
  deleteInvestor: (id: string) => Promise<void>;

  // Cash flow actions
  importCashFlow: (records: CashFlowRecord[]) => Promise<{ imported: number; errors: string[] }>;
  updateCashFlowField: (month: string, field: string, value: number) => Promise<void>;
  createCashFlowRecord: (record: Partial<CashFlowRecord>) => Promise<CashFlowRecord>;

  // Clear error
  clearError: () => void;
}

// ============================================
// SQL HELPERS
// ============================================

/**
 * Generate a UUID for new records
 */
function generateId(): string {
  return crypto.randomUUID();
}

/**
 * Track DB version before mutation for change detection
 */
let lastDbVersion: bigint = BigInt(0);

/**
 * Capture current DB version (call before mutation)
 */
async function captureVersion(): Promise<void> {
  try {
    lastDbVersion = await databaseService.getDbVersion();
  } catch {
    lastDbVersion = BigInt(0);
  }
}

/**
 * Broadcast changes since last captured version (call after mutation)
 */
async function broadcastChanges(): Promise<void> {
  try {
    const syncStore = useSyncStore.getState();
    if (!syncStore.activeSpaceId || syncStore.status !== 'connected') {
      return; // Not syncing
    }

    // Small delay to ensure CRDT changes are committed
    await new Promise(resolve => setTimeout(resolve, 50));

    // Broadcast changes to the active space
    await syncService.broadcastChanges(syncStore.activeSpaceId, lastDbVersion);
  } catch (err) {
    console.warn('[DataStore] Failed to broadcast changes:', err);
  }
}

/**
 * Safely convert a timestamp to ISO string
 */
function toISOString(value: unknown): string {
  if (!value) return new Date().toISOString();
  if (typeof value === 'number') {
    return new Date(value).toISOString();
  }
  if (typeof value === 'string') {
    // Already an ISO string or date string
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? new Date().toISOString() : parsed.toISOString();
  }
  return new Date().toISOString();
}

/**
 * Convert SQLite row to Task
 */
function rowToTask(row: Record<string, unknown>): Task {
  // Debug: Log raw is_signal value from SQLite
  if (row.is_signal) {
    console.log('[DataStore] rowToTask - raw is_signal value:', row.is_signal, 'type:', typeof row.is_signal, 'Boolean result:', Boolean(row.is_signal));
  }
  return {
    id: row.id as string,
    airtableId: row.id as string, // Use same ID for compatibility
    title: row.title as string,
    domain: row.domain as string,
    owner: row.owner as string,
    status: (row.status as string) || 'Not Started',
    priority: (row.priority as string) || 'Medium',
    isSignal: Boolean(row.is_signal),
    targetDate: row.target_date as string | null,
    createdAt: toISOString(row.created_at),
    updatedAt: row.updated_at ? toISOString(row.updated_at) : undefined,
    notes: row.notes as string | undefined,
    track: row.track as string | undefined,
  } as Task;
}

/**
 * Convert SQLite row to Deal
 */
function rowToDeal(row: Record<string, unknown>): Deal {
  return {
    id: row.id as string,
    airtableId: row.id as string,
    prospect: row.prospect as string,
    owner: row.owner as string,
    probability: (row.probability as number) || 0,
    tcv: (row.tcv as number) || 0,
    acv2026: row.acv_2026 as number | undefined,
    closeDate: row.close_date as string | null,
    stage: (row.stage as string) || 'Qualification',
    currency: (row.currency as 'EUR' | 'USD') || 'EUR',
    createdAt: toISOString(row.created_at),
    description: row.description as string | undefined,
    nextSteps: row.next_steps as string | undefined,
  } as Deal;
}

/**
 * Convert SQLite row to Client
 */
function rowToClient(row: Record<string, unknown>): Client {
  return {
    id: row.id as string,
    airtableId: row.id as string,
    name: row.name as string,
    owner: row.owner as string,
    health: (row.health as string) || 'Healthy',
    acv2026: row.acv_2026 as number | undefined,
    contractStart: row.contract_start as string | undefined,
    contractEnd: row.contract_end as string | undefined,
    industry: row.industry as string | undefined,
    region: row.region as string | undefined,
    createdAt: toISOString(row.created_at),
    notes: row.notes as string | undefined,
  } as Client;
}

/**
 * Convert SQLite row to Owner
 */
function rowToOwner(row: Record<string, unknown>): Owner {
  return {
    id: row.id as string,
    airtableId: row.id as string,
    name: row.name as string,
    email: row.email as string | undefined,
    role: row.role as string | undefined,
    department: row.department as string | undefined,
    active: Boolean(row.active),
    createdAt: toISOString(row.created_at),
  };
}

/**
 * Convert SQLite row to CashFlowRecord
 */
function rowToCashFlow(row: Record<string, unknown>): CashFlowRecord {
  return {
    id: row.id as string,
    airtableId: row.id as string,
    month: row.month as string,
    monthDate: row.month_date as string,
    recurringRevenue: (row.recurring_revenue as number) || 0,
    newDealRevenue: (row.new_deal_revenue as number) || 0,
    personnelCost: (row.personnel_cost as number) || 0,
    infrastructureCost: (row.infrastructure_cost as number) || 0,
    marketingCost: (row.marketing_cost as number) || 0,
    legalCost: (row.legal_cost as number) || 0,
    otherCost: (row.other_cost as number) || 0,
    balance: row.balance as number | undefined,
    isForecast: Boolean(row.is_forecast),
    createdAt: toISOString(row.created_at),
  };
}

/**
 * Convert SQLite row to Investor
 */
function rowToInvestor(row: Record<string, unknown>): Investor {
  return {
    id: row.id as string,
    name: row.name as string,
    stage: (row.stage as string) || 'Research',
    contactName: row.contact_name as string | undefined,
    email: row.email as string | undefined,
    lastContact: row.last_contact as string | undefined,
    targetAmount: row.target_amount as number | undefined,
    notes: row.notes as string | undefined,
    createdAt: row.created_at ? toISOString(row.created_at) : undefined,
  } as Investor;
}

/**
 * Convert SQLite row to MAURecord
 */
function rowToMAU(row: Record<string, unknown>): MAURecord {
  return {
    month: row.month as string,
    monthDate: row.month_date as string,
    mau: (row.mau as number) || 0,
    growth: row.growth as number | undefined,
  };
}

// ============================================
// STORE
// ============================================

export const useDataStoreSQLite = create<DataState>()(
  devtools(
    (set, get) => ({
      // Initial state
      tasks: [],
      deals: [],
      clients: [],
      owners: [],
      cashflow: [],
      investors: [],
      mauData: [],
      signals: [],
      loading: false,
      loadingTasks: false,
      loadingDeals: false,
      error: null,
      lastRefresh: null,

      // ============================================
      // FETCH ALL
      // ============================================

      fetchAll: async () => {
        const syncStore = useSyncStore.getState();
        if (!syncStore.initialized) {
          console.warn('[DataStore] Sync store not initialized, cannot fetch data');
          return;
        }

        console.log('[DataStore] Starting fetchAll...');
        set({ loading: true, error: null });

        try {
          // Fetch all data from SQLite
          const [
            tasksResult,
            dealsResult,
            clientsResult,
            ownersResult,
            cashflowResult,
            investorsResult,
            mauResult,
          ] = await Promise.all([
            databaseService.exec<Record<string, unknown>[]>(
              'SELECT * FROM tasks ORDER BY created_at DESC'
            ),
            databaseService.exec<Record<string, unknown>[]>(
              'SELECT * FROM deals ORDER BY created_at DESC'
            ),
            databaseService.exec<Record<string, unknown>[]>(
              'SELECT * FROM clients ORDER BY name'
            ),
            databaseService.exec<Record<string, unknown>[]>(
              'SELECT * FROM owners ORDER BY name'
            ),
            databaseService.exec<Record<string, unknown>[]>(
              'SELECT * FROM cashflow ORDER BY month_date'
            ),
            databaseService.exec<Record<string, unknown>[]>(
              'SELECT * FROM investors ORDER BY created_at DESC'
            ),
            databaseService.exec<Record<string, unknown>[]>(
              'SELECT * FROM mau_data ORDER BY month_date'
            ),
          ]);

          // Transform to app types
          const tasks = (tasksResult as unknown as Record<string, unknown>[]).map(rowToTask);
          const deals = (dealsResult as unknown as Record<string, unknown>[]).map(rowToDeal);
          const clients = (clientsResult as unknown as Record<string, unknown>[]).map(rowToClient);
          const owners = (ownersResult as unknown as Record<string, unknown>[]).map(rowToOwner);
          const cashflow = (cashflowResult as unknown as Record<string, unknown>[]).map(rowToCashFlow);
          const investors = (investorsResult as unknown as Record<string, unknown>[]).map(rowToInvestor);
          const mauData = (mauResult as unknown as Record<string, unknown>[]).map(rowToMAU);

          console.log('[DataStore] Loaded data counts:', {
            tasks: tasks.length,
            deals: deals.length,
            clients: clients.length,
            owners: owners.length,
            cashflow: cashflow.length,
            investors: investors.length,
            mauData: mauData.length,
          });

          set({
            tasks,
            deals,
            clients,
            owners,
            cashflow,
            investors,
            mauData,
            loading: false,
            lastRefresh: new Date().toISOString(),
          });

          // Refresh signals after data load
          get().refreshSignals();
          console.log('[DataStore] fetchAll complete');
        } catch (error) {
          console.error('[DataStore] Failed to fetch data:', error);
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to fetch data',
          });
        }
      },

      // ============================================
      // FETCH INDIVIDUAL TABLES
      // ============================================

      fetchTasks: async () => {
        set({ loadingTasks: true });
        try {
          const result = await databaseService.exec<Record<string, unknown>[]>(
            'SELECT * FROM tasks ORDER BY created_at DESC'
          );
          const tasks = (result as unknown as Record<string, unknown>[]).map(rowToTask);
          set({ tasks, loadingTasks: false });
          get().refreshSignals();
        } catch (error) {
          console.error('Failed to fetch tasks:', error);
          set({ loadingTasks: false });
        }
      },

      fetchDeals: async () => {
        set({ loadingDeals: true });
        try {
          const result = await databaseService.exec<Record<string, unknown>[]>(
            'SELECT * FROM deals ORDER BY created_at DESC'
          );
          const deals = (result as unknown as Record<string, unknown>[]).map(rowToDeal);
          set({ deals, loadingDeals: false });
          get().refreshSignals();
        } catch (error) {
          console.error('Failed to fetch deals:', error);
          set({ loadingDeals: false });
        }
      },

      // ============================================
      // SIGNALS
      // ============================================

      refreshSignals: () => {
        const { tasks, deals, clients, cashflow, investors, mauData } = get();

        // Also include initiative tasks from initiativeStore
        const initiativeStore = useInitiativeStore.getState();
        const initiativeTasks = initiativeStore.tasks || [];
        const initiatives = initiativeStore.initiatives || [];

        // Convert initiative tasks to Task format for signal detection
        const convertedInitiativeTasks: Task[] = initiativeTasks.map(t => {
          const initiative = initiatives.find(i => i.id === t.initiativeId);
          return {
            id: t.id,
            title: t.title,
            status: t.status,
            priority: t.priority,
            targetDate: t.targetDate || null,
            isSignal: t.isSignal || false,
            owner: t.owner || '',
            domain: initiative?.name || 'Initiative',
            createdAt: t.createdAt,
            updatedAt: t.updatedAt,
          };
        });

        // Combine all tasks for signal detection
        const allTasks = [...tasks, ...convertedInitiativeTasks];

        console.log('[DataStore] refreshSignals called with', tasks.length, 'regular tasks +', convertedInitiativeTasks.length, 'initiative tasks');
        console.log('[DataStore] Tasks with isSignal=true:', allTasks.filter(t => t.isSignal).map(t => ({ id: t.id, title: t.title, isSignal: t.isSignal })));

        const signals = SignalEngine.detectSignals({
          tasks: allTasks,
          deals,
          clients,
          cashflow,
          investors,
          mauData,
        });
        console.log('[DataStore] Generated signals:', signals.length, signals.map(s => ({ id: s.id, type: s.type, title: s.title })));
        set({ signals });
      },

      // ============================================
      // TASK CRUD
      // ============================================

      createTask: async (taskData) => {
        console.log('[DataStore] Creating task:', taskData.title);
        await captureVersion();

        const id = generateId();
        const now = Date.now();

        // Get active space_id from sync store
        const syncStore = useSyncStore.getState();
        const spaceId = syncStore.activeSpaceId || 'default';
        console.log('[DataStore] Using space_id:', spaceId);

        await databaseService.run(
          `INSERT INTO tasks (id, space_id, title, domain, owner, status, priority, is_signal, target_date, notes, track, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            id,
            spaceId,
            taskData.title || '',
            taskData.domain || 'EMG',
            taskData.owner || '',
            taskData.status || 'Not Started',
            taskData.priority || 'Medium',
            taskData.isSignal ? 1 : 0,
            taskData.targetDate || null,
            taskData.notes || null,
            taskData.track || null,
            now,
            now,
          ]
        );
        console.log('[DataStore] Task inserted into SQLite with id:', id);

        const task: Task = {
          id,
          airtableId: id,
          title: taskData.title || '',
          domain: taskData.domain || 'EMG',
          owner: taskData.owner || '',
          status: taskData.status || 'Not Started',
          priority: taskData.priority || 'Medium',
          isSignal: taskData.isSignal || false,
          targetDate: taskData.targetDate || null,
          createdAt: new Date(now).toISOString(),
          updatedAt: new Date(now).toISOString(),
          notes: taskData.notes,
          track: taskData.track,
        };

        set((state) => ({ tasks: [...state.tasks, task] }));
        get().refreshSignals();

        // Broadcast to peers
        broadcastChanges();

        // Verify the task was persisted
        try {
          const verifyResult = await databaseService.exec<Record<string, unknown>[]>(
            'SELECT COUNT(*) as count FROM tasks'
          );
          console.log('[DataStore] Verified task count in DB:', verifyResult);
        } catch (e) {
          console.error('[DataStore] Failed to verify task:', e);
        }

        return task;
      },

      updateTask: async (id, updates) => {
        await captureVersion();

        const now = Date.now();
        const currentTask = get().tasks.find((t) => t.id === id);
        if (!currentTask) {
          throw new Error(`Task ${id} not found`);
        }

        // Build dynamic update query
        const fields: string[] = [];
        const values: unknown[] = [];

        if (updates.title !== undefined) {
          fields.push('title = ?');
          values.push(updates.title);
        }
        if (updates.domain !== undefined) {
          fields.push('domain = ?');
          values.push(updates.domain);
        }
        if (updates.owner !== undefined) {
          fields.push('owner = ?');
          values.push(updates.owner);
        }
        if (updates.status !== undefined) {
          fields.push('status = ?');
          values.push(updates.status);
        }
        if (updates.priority !== undefined) {
          fields.push('priority = ?');
          values.push(updates.priority);
        }
        if (updates.isSignal !== undefined) {
          fields.push('is_signal = ?');
          values.push(updates.isSignal ? 1 : 0);
        }
        if (updates.targetDate !== undefined) {
          fields.push('target_date = ?');
          values.push(updates.targetDate);
        }
        if (updates.notes !== undefined) {
          fields.push('notes = ?');
          values.push(updates.notes);
        }
        if (updates.track !== undefined) {
          fields.push('track = ?');
          values.push(updates.track);
        }

        fields.push('updated_at = ?');
        values.push(now);
        values.push(id);

        await databaseService.run(
          `UPDATE tasks SET ${fields.join(', ')} WHERE id = ?`,
          values
        );

        const updatedTask: Task = {
          ...currentTask,
          ...updates,
          updatedAt: new Date(now).toISOString(),
        };

        set((state) => ({
          tasks: state.tasks.map((t) => (t.id === id ? updatedTask : t)),
        }));
        get().refreshSignals();

        // Broadcast to peers
        broadcastChanges();

        return updatedTask;
      },

      deleteTask: async (id) => {
        await captureVersion();

        await databaseService.run('DELETE FROM tasks WHERE id = ?', [id]);
        set((state) => ({
          tasks: state.tasks.filter((t) => t.id !== id),
        }));
        get().refreshSignals();

        // Broadcast to peers
        broadcastChanges();
      },

      toggleTaskStatus: async (id) => {
        const task = get().tasks.find((t) => t.id === id);
        if (!task) return;

        const newStatus = task.status === 'Done' ? 'Not Started' : 'Done';
        await get().updateTask(id, { status: newStatus });
      },

      toggleTaskSignal: async (id) => {
        const task = get().tasks.find((t) => t.id === id);
        if (!task) return;

        await get().updateTask(id, { isSignal: !task.isSignal });
      },

      // ============================================
      // DEAL CRUD
      // ============================================

      createDeal: async (dealData) => {
        await captureVersion();

        const id = generateId();
        const now = Date.now();

        // Get active space_id from sync store
        const syncStore = useSyncStore.getState();
        const spaceId = syncStore.activeSpaceId || 'default';

        await databaseService.run(
          `INSERT INTO deals (id, space_id, prospect, owner, probability, tcv, acv_2026, close_date, stage, currency, description, next_steps, created_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            id,
            spaceId,
            dealData.prospect || '',
            dealData.owner || '',
            dealData.probability || 0,
            dealData.tcv || 0,
            dealData.acv2026 || null,
            dealData.closeDate || null,
            dealData.stage || 'Qualification',
            dealData.currency || 'EUR',
            dealData.description || null,
            dealData.nextSteps || null,
            now,
          ]
        );

        const deal: Deal = {
          id,
          airtableId: id,
          prospect: dealData.prospect || '',
          owner: dealData.owner || '',
          probability: dealData.probability || 0,
          tcv: dealData.tcv || 0,
          acv2026: dealData.acv2026,
          closeDate: dealData.closeDate || null,
          stage: dealData.stage || 'Qualification',
          currency: dealData.currency || 'EUR',
          createdAt: new Date(now).toISOString(),
          description: dealData.description,
          nextSteps: dealData.nextSteps,
        };

        set((state) => ({ deals: [...state.deals, deal] }));
        get().refreshSignals();

        // Broadcast to peers
        broadcastChanges();
        return deal;
      },

      updateDeal: async (id, updates) => {
        await captureVersion();

        const currentDeal = get().deals.find((d) => d.id === id);
        if (!currentDeal) {
          throw new Error(`Deal ${id} not found`);
        }

        const fields: string[] = [];
        const values: unknown[] = [];

        if (updates.prospect !== undefined) {
          fields.push('prospect = ?');
          values.push(updates.prospect);
        }
        if (updates.owner !== undefined) {
          fields.push('owner = ?');
          values.push(updates.owner);
        }
        if (updates.probability !== undefined) {
          fields.push('probability = ?');
          values.push(updates.probability);
        }
        if (updates.tcv !== undefined) {
          fields.push('tcv = ?');
          values.push(updates.tcv);
        }
        if (updates.acv2026 !== undefined) {
          fields.push('acv_2026 = ?');
          values.push(updates.acv2026);
        }
        if (updates.closeDate !== undefined) {
          fields.push('close_date = ?');
          values.push(updates.closeDate);
        }
        if (updates.stage !== undefined) {
          fields.push('stage = ?');
          values.push(updates.stage);
        }
        if (updates.currency !== undefined) {
          fields.push('currency = ?');
          values.push(updates.currency);
        }
        if (updates.description !== undefined) {
          fields.push('description = ?');
          values.push(updates.description);
        }
        if (updates.nextSteps !== undefined) {
          fields.push('next_steps = ?');
          values.push(updates.nextSteps);
        }

        values.push(id);

        if (fields.length > 0) {
          await databaseService.run(
            `UPDATE deals SET ${fields.join(', ')} WHERE id = ?`,
            values
          );
        }

        const updatedDeal: Deal = {
          ...currentDeal,
          ...updates,
        };

        set((state) => ({
          deals: state.deals.map((d) => (d.id === id ? updatedDeal : d)),
        }));
        get().refreshSignals();

        // Broadcast to peers
        broadcastChanges();

        return updatedDeal;
      },

      deleteDeal: async (id) => {
        await captureVersion();

        await databaseService.run('DELETE FROM deals WHERE id = ?', [id]);
        set((state) => ({
          deals: state.deals.filter((d) => d.id !== id),
        }));
        get().refreshSignals();

        // Broadcast to peers
        broadcastChanges();
      },

      // ============================================
      // CLIENT CRUD
      // ============================================

      createClient: async (clientData) => {
        await captureVersion();

        const id = generateId();
        const now = Date.now();

        // Get active space_id from sync store
        const syncStore = useSyncStore.getState();
        const spaceId = syncStore.activeSpaceId || 'default';

        await databaseService.run(
          `INSERT INTO clients (id, space_id, name, owner, health, acv_2026, contract_start, contract_end, industry, region, notes, created_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            id,
            spaceId,
            clientData.name || '',
            clientData.owner || '',
            clientData.health || 'Healthy',
            clientData.acv2026 || null,
            clientData.contractStart || null,
            clientData.contractEnd || null,
            clientData.industry || null,
            clientData.region || null,
            clientData.notes || null,
            now,
          ]
        );

        const client: Client = {
          id,
          airtableId: id,
          name: clientData.name || '',
          owner: clientData.owner || '',
          health: clientData.health || 'Healthy',
          acv2026: clientData.acv2026,
          contractStart: clientData.contractStart,
          contractEnd: clientData.contractEnd,
          industry: clientData.industry,
          region: clientData.region,
          createdAt: new Date(now).toISOString(),
          notes: clientData.notes,
        };

        set((state) => ({ clients: [...state.clients, client] }));

        // Refresh signals and broadcast to peers
        get().refreshSignals();
        broadcastChanges();

        return client;
      },

      updateClient: async (id, updates) => {
        await captureVersion();

        const currentClient = get().clients.find((c) => c.id === id);
        if (!currentClient) {
          throw new Error(`Client ${id} not found`);
        }

        const fields: string[] = [];
        const values: unknown[] = [];

        if (updates.name !== undefined) {
          fields.push('name = ?');
          values.push(updates.name);
        }
        if (updates.owner !== undefined) {
          fields.push('owner = ?');
          values.push(updates.owner);
        }
        if (updates.health !== undefined) {
          fields.push('health = ?');
          values.push(updates.health);
        }
        if (updates.acv2026 !== undefined) {
          fields.push('acv_2026 = ?');
          values.push(updates.acv2026);
        }
        if (updates.contractStart !== undefined) {
          fields.push('contract_start = ?');
          values.push(updates.contractStart);
        }
        if (updates.contractEnd !== undefined) {
          fields.push('contract_end = ?');
          values.push(updates.contractEnd);
        }
        if (updates.industry !== undefined) {
          fields.push('industry = ?');
          values.push(updates.industry);
        }
        if (updates.region !== undefined) {
          fields.push('region = ?');
          values.push(updates.region);
        }
        if (updates.notes !== undefined) {
          fields.push('notes = ?');
          values.push(updates.notes);
        }

        values.push(id);

        if (fields.length > 0) {
          await databaseService.run(
            `UPDATE clients SET ${fields.join(', ')} WHERE id = ?`,
            values
          );
        }

        const updatedClient: Client = {
          ...currentClient,
          ...updates,
        };

        set((state) => ({
          clients: state.clients.map((c) => (c.id === id ? updatedClient : c)),
        }));

        // Refresh signals and broadcast to peers
        get().refreshSignals();
        broadcastChanges();

        return updatedClient;
      },

      deleteClient: async (id) => {
        await captureVersion();

        await databaseService.run('DELETE FROM clients WHERE id = ?', [id]);
        set((state) => ({
          clients: state.clients.filter((c) => c.id !== id),
        }));

        // Refresh signals and broadcast to peers
        get().refreshSignals();
        broadcastChanges();
      },

      // ============================================
      // OWNER CRUD (Local SQLite)
      // ============================================

      createOwner: async (ownerData) => {
        await captureVersion();

        const id = generateId();
        const now = Date.now();

        // Get active space_id from sync store
        const syncStore = useSyncStore.getState();
        const spaceId = syncStore.activeSpaceId || 'default';

        await databaseService.run(
          `INSERT INTO owners (id, space_id, name, email, role, department, active, created_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            id,
            spaceId,
            ownerData.name || '',
            ownerData.email || null,
            ownerData.role || null,
            ownerData.department || null,
            ownerData.active !== false ? 1 : 0,
            now,
          ]
        );

        const owner: Owner = {
          id,
          airtableId: id,
          name: ownerData.name || '',
          email: ownerData.email,
          role: ownerData.role,
          department: ownerData.department,
          active: ownerData.active !== false,
          createdAt: new Date(now).toISOString(),
        };

        set((state) => ({ owners: [...state.owners, owner] }));

        // Broadcast to peers
        broadcastChanges();

        return owner;
      },

      updateOwner: async (id, updates) => {
        await captureVersion();

        const currentOwner = get().owners.find((o) => o.id === id);
        if (!currentOwner) {
          throw new Error(`Owner ${id} not found`);
        }

        const fields: string[] = [];
        const values: unknown[] = [];

        if (updates.name !== undefined) {
          fields.push('name = ?');
          values.push(updates.name);
        }
        if (updates.email !== undefined) {
          fields.push('email = ?');
          values.push(updates.email);
        }
        if (updates.role !== undefined) {
          fields.push('role = ?');
          values.push(updates.role);
        }
        if (updates.department !== undefined) {
          fields.push('department = ?');
          values.push(updates.department);
        }
        if (updates.active !== undefined) {
          fields.push('active = ?');
          values.push(updates.active ? 1 : 0);
        }

        values.push(id);

        if (fields.length > 0) {
          await databaseService.run(
            `UPDATE owners SET ${fields.join(', ')} WHERE id = ?`,
            values
          );
        }

        const updatedOwner: Owner = {
          ...currentOwner,
          ...updates,
        };

        set((state) => ({
          owners: state.owners.map((o) => (o.id === id ? updatedOwner : o)),
        }));

        // Broadcast to peers
        broadcastChanges();

        return updatedOwner;
      },

      deleteOwner: async (id) => {
        await captureVersion();

        await databaseService.run('DELETE FROM owners WHERE id = ?', [id]);
        set((state) => ({
          owners: state.owners.filter((o) => o.id !== id),
        }));

        // Broadcast to peers
        broadcastChanges();
      },

      // ============================================
      // INVESTOR CRUD (Local SQLite)
      // ============================================

      addInvestor: async (investor) => {
        const id = investor.id || generateId();
        const now = Date.now();

        // Get active space_id from sync store
        const syncStore = useSyncStore.getState();
        const spaceId = syncStore.activeSpaceId || 'default';

        try {
          await databaseService.run(
            `INSERT INTO investors (id, space_id, name, stage, contact_name, email, last_contact, target_amount, notes, created_at)
             VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
            [
              id,
              spaceId,
              investor.name,
              investor.stage || 'Research',
              investor.contactName || null,
              investor.email || null,
              investor.lastContact || null,
              investor.targetAmount || null,
              investor.notes || null,
              now,
            ]
          );
        } catch (err) {
          console.error('Failed to save investor:', err);
          throw err;
        }

        const newInvestor: Investor = {
          ...investor,
          id,
          createdAt: new Date(now).toISOString(),
        };

        set((state) => ({ investors: [...state.investors, newInvestor] }));
        get().refreshSignals();
        broadcastChanges();
      },

      updateInvestor: async (id, updates) => {
        const fields: string[] = [];
        const values: unknown[] = [];

        if (updates.name !== undefined) {
          fields.push('name = ?');
          values.push(updates.name);
        }
        if (updates.stage !== undefined) {
          fields.push('stage = ?');
          values.push(updates.stage);
        }
        if (updates.contactName !== undefined) {
          fields.push('contact_name = ?');
          values.push(updates.contactName);
        }
        if (updates.email !== undefined) {
          fields.push('email = ?');
          values.push(updates.email);
        }
        if (updates.lastContact !== undefined) {
          fields.push('last_contact = ?');
          values.push(updates.lastContact);
        }
        if (updates.targetAmount !== undefined) {
          fields.push('target_amount = ?');
          values.push(updates.targetAmount);
        }
        if (updates.notes !== undefined) {
          fields.push('notes = ?');
          values.push(updates.notes);
        }

        values.push(id);

        if (fields.length > 0) {
          try {
            await databaseService.run(
              `UPDATE investors SET ${fields.join(', ')} WHERE id = ?`,
              values
            );
          } catch (err) {
            console.error('Failed to update investor:', err);
            throw err;
          }
        }

        set((state) => ({
          investors: state.investors.map((i) =>
            i.id === id ? { ...i, ...updates } : i
          ),
        }));
        get().refreshSignals();
        broadcastChanges();
      },

      deleteInvestor: async (id) => {
        try {
          await databaseService.run('DELETE FROM investors WHERE id = ?', [id]);
        } catch (err) {
          console.error('Failed to delete investor:', err);
          throw err;
        }

        set((state) => ({
          investors: state.investors.filter((i) => i.id !== id),
        }));
        get().refreshSignals();
        broadcastChanges();
      },

      // ============================================
      // CASH FLOW
      // ============================================

      importCashFlow: async (records) => {
        const errors: string[] = [];
        let imported = 0;

        // Get active space_id from sync store
        const syncStore = useSyncStore.getState();
        const spaceId = syncStore.activeSpaceId || 'default';

        for (const record of records) {
          try {
            const id = record.id || generateId();
            const now = Date.now();

            // Check if record already exists (by month)
            const existing = await databaseService.exec<{ id: string }>(
              'SELECT id FROM cashflow WHERE space_id = ? AND month = ?',
              [spaceId, record.month]
            );

            if (existing.length > 0) {
              // Update existing record
              await databaseService.run(
                `UPDATE cashflow SET
                  recurring_revenue = ?,
                  new_deal_revenue = ?,
                  personnel_cost = ?,
                  infrastructure_cost = ?,
                  marketing_cost = ?,
                  legal_cost = ?,
                  other_cost = ?,
                  balance = ?,
                  is_forecast = ?
                WHERE id = ?`,
                [
                  record.recurringRevenue,
                  record.newDealRevenue,
                  record.personnelCost,
                  record.infrastructureCost,
                  record.marketingCost,
                  record.legalCost,
                  record.otherCost,
                  record.balance || null,
                  record.isForecast ? 1 : 0,
                  existing[0].id,
                ]
              );
            } else {
              // Insert new record
              await databaseService.run(
                `INSERT INTO cashflow (id, space_id, month, month_date, recurring_revenue, new_deal_revenue, personnel_cost, infrastructure_cost, marketing_cost, legal_cost, other_cost, balance, is_forecast, created_at)
                 VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
                [
                  id,
                  spaceId,
                  record.month,
                  record.monthDate,
                  record.recurringRevenue,
                  record.newDealRevenue,
                  record.personnelCost,
                  record.infrastructureCost,
                  record.marketingCost,
                  record.legalCost,
                  record.otherCost,
                  record.balance || null,
                  record.isForecast ? 1 : 0,
                  now,
                ]
              );
            }
            imported++;
          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            errors.push(`${record.month}: ${msg}`);
            console.error('Failed to import cash flow record:', err);
          }
        }

        // Reload cash flow data from database
        try {
          const result = await databaseService.exec<{
            id: string;
            month: string;
            month_date: string;
            recurring_revenue: number;
            new_deal_revenue: number;
            personnel_cost: number;
            infrastructure_cost: number;
            marketing_cost: number;
            legal_cost: number;
            other_cost: number;
            balance: number | null;
            is_forecast: number;
            created_at: number;
          }>(
            'SELECT * FROM cashflow WHERE space_id = ? ORDER BY month_date',
            [spaceId]
          );

          const cashflow: CashFlowRecord[] = result.map((row) => ({
            id: row.id,
            airtableId: '',
            month: row.month,
            monthDate: row.month_date,
            recurringRevenue: row.recurring_revenue,
            newDealRevenue: row.new_deal_revenue,
            personnelCost: row.personnel_cost,
            infrastructureCost: row.infrastructure_cost,
            marketingCost: row.marketing_cost,
            legalCost: row.legal_cost,
            otherCost: row.other_cost,
            balance: row.balance || undefined,
            isForecast: row.is_forecast === 1,
            createdAt: new Date(row.created_at).toISOString(),
          }));

          set({ cashflow });
        } catch (err) {
          console.error('Failed to reload cash flow data:', err);
        }

        get().refreshSignals();
        broadcastChanges();

        return { imported, errors };
      },

      updateCashFlowField: async (month, field, value) => {
        // Get active space_id from sync store
        const syncStore = useSyncStore.getState();
        const spaceId = syncStore.activeSpaceId || 'default';

        // Map camelCase field names to snake_case database columns
        const fieldMap: Record<string, string> = {
          recurringRevenue: 'recurring_revenue',
          newDealRevenue: 'new_deal_revenue',
          personnelCost: 'personnel_cost',
          infrastructureCost: 'infrastructure_cost',
          marketingCost: 'marketing_cost',
          legalCost: 'legal_cost',
          otherCost: 'other_cost',
          balance: 'balance',
        };

        const dbField = fieldMap[field];
        if (!dbField) {
          throw new Error(`Invalid field: ${field}`);
        }

        // Find the record by month
        const existing = await databaseService.exec<{ id: string }>(
          'SELECT id FROM cashflow WHERE space_id = ? AND LOWER(month) = LOWER(?)',
          [spaceId, month]
        );

        if (existing.length === 0) {
          throw new Error(`No cash flow record found for ${month}`);
        }

        await databaseService.run(
          `UPDATE cashflow SET ${dbField} = ? WHERE id = ?`,
          [value, existing[0].id]
        );

        // Update local state
        set((state) => ({
          cashflow: state.cashflow.map((cf) =>
            cf.month.toLowerCase() === month.toLowerCase()
              ? { ...cf, [field]: value }
              : cf
          ),
        }));

        get().refreshSignals();
        broadcastChanges();
      },

      createCashFlowRecord: async (record) => {
        const syncStore = useSyncStore.getState();
        const spaceId = syncStore.activeSpaceId || 'default';
        const id = generateId();
        const now = Date.now();

        // Parse month to create monthDate if not provided
        const monthDate = record.monthDate || (() => {
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          const parts = (record.month || '').split(' ');
          if (parts.length === 2) {
            const monthIndex = monthNames.findIndex(m => m.toLowerCase() === parts[0].toLowerCase());
            const year = parseInt(parts[1], 10);
            if (monthIndex >= 0 && year) {
              return `${year}-${String(monthIndex + 1).padStart(2, '0')}-01`;
            }
          }
          return new Date().toISOString().split('T')[0];
        })();

        await databaseService.run(
          `INSERT INTO cashflow (id, space_id, month, month_date, recurring_revenue, new_deal_revenue, personnel_cost, infrastructure_cost, marketing_cost, legal_cost, other_cost, balance, is_forecast, created_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            id,
            spaceId,
            record.month || '',
            monthDate,
            record.recurringRevenue || 0,
            record.newDealRevenue || 0,
            record.personnelCost || 0,
            record.infrastructureCost || 0,
            record.marketingCost || 0,
            record.legalCost || 0,
            record.otherCost || 0,
            record.balance || null,
            record.isForecast ? 1 : 0,
            now,
          ]
        );

        const newRecord: CashFlowRecord = {
          id,
          airtableId: '',
          month: record.month || '',
          monthDate,
          recurringRevenue: record.recurringRevenue || 0,
          newDealRevenue: record.newDealRevenue || 0,
          personnelCost: record.personnelCost || 0,
          infrastructureCost: record.infrastructureCost || 0,
          marketingCost: record.marketingCost || 0,
          legalCost: record.legalCost || 0,
          otherCost: record.otherCost || 0,
          balance: record.balance,
          isForecast: record.isForecast,
          createdAt: new Date(now).toISOString(),
        };

        set((state) => ({
          cashflow: [...state.cashflow, newRecord].sort(
            (a, b) => new Date(a.monthDate).getTime() - new Date(b.monthDate).getTime()
          ),
        }));

        get().refreshSignals();
        broadcastChanges();

        return newRecord;
      },

      // ============================================
      // UTILITY
      // ============================================

      clearError: () => set({ error: null }),
    }),
    { name: 'ceo-data-store-sqlite' }
  )
);

// ============================================
// SELECTORS
// ============================================

export const useTasksByDomainSQLite = (domain: string) => {
  return useDataStoreSQLite((state) =>
    (state.tasks || []).filter((t) => t.domain === domain)
  );
};

export const useActiveDealsSQLite = () => {
  return useDataStoreSQLite((state) =>
    (state.deals || []).filter(
      (d) => !['Closed Won', 'Closed Lost'].includes(d.stage)
    )
  );
};

export const useSignalTasksSQLite = () => {
  return useDataStoreSQLite((state) =>
    (state.tasks || []).filter((t) => t.isSignal && t.status !== 'Done')
  );
};

export const useOverdueTasksSQLite = () => {
  const today = new Date().toISOString().split('T')[0];
  return useDataStoreSQLite((state) =>
    (state.tasks || []).filter(
      (t) => t.targetDate && t.targetDate < today && t.status !== 'Done'
    )
  );
};

export const useTodayTasksSQLite = () => {
  const today = new Date().toISOString().split('T')[0];
  return useDataStoreSQLite((state) =>
    (state.tasks || []).filter(
      (t) => t.targetDate === today && t.status !== 'Done'
    )
  );
};

export const useSignalCountsSQLite = () => {
  return useDataStoreSQLite((state) => {
    const signals = state.signals || [];
    return {
      critical: signals.filter((s) => s.level === 'critical').length,
      high: signals.filter((s) => s.level === 'high').length,
      medium: signals.filter((s) => s.level === 'medium').length,
      low: signals.filter((s) => s.level === 'low').length,
      total: signals.length,
    };
  });
};

export const useBoardReadinessSQLite = () => {
  return useDataStoreSQLite((state) => {
    const { tasks, deals, clients, cashflow, mauData } = state;
    return SignalEngine.calculateBoardReadiness({
      tasks,
      deals,
      clients,
      cashflow,
      mauData,
    });
  });
};
